import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
  type ServerError,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { datadogLogs } from '@datadog/browser-logs';
import type { LanguageCodes } from '@onevesthq/ov-enums';
import type React from 'react';
import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBackend } from '../assets/i18n/config';
import { CONFIG } from '../config';
import { clientHostedLogout } from './AuthProvider';
import { auth0Logout } from './auth0Client';
import { useGlobalToast } from './globalToastProvider';

const PUBLIC_ENDPOINTS = [
  'fetchOrganizationPublicSettings',
  'exchangeClientToken',
];

type AuthContextType = {
  isEmbedded: boolean;
  setIsEmbedded: (embedded: boolean) => void;
  settings: AuthContextSettings;
  setSettings: (settings: AuthContextSettings) => void;
  permissions: string[];
  entityRoleIdRef: { current: string | undefined };
  languageRef: { current: LanguageCodes | undefined };
  getTokenSilentlyRef: { current: (() => Promise<string>) | undefined };
  setPermissions: (permissions: string[]) => void;
  appLogout: (input?: {
    reason?: string;
    preventSingleLogout?: boolean;
  }) => Promise<void>;
};

export type AuthContextSettings = {
  clientHostedLoginRedirectUri?: string;
  clientHostedLoginURL?: string;
  clientHostedLoginClientId?: string;
  subdomain?: string;
  auth0RedirectUri?: string;
  enableInactivityTimeOut?: boolean;
  inactivityTimeoutInMinutes?: number;
  useHostedLogin?: boolean;
  logoutUrl?: string;
  enableSingleLogout?: boolean;
};

const AuthContext = createContext<AuthContextType>({
  isEmbedded: false,
  setIsEmbedded: () => {},
  settings: {},
  setSettings: () => {},
  permissions: [],
  setPermissions: () => {},
  appLogout: async (
    _input: { reason?: string; preventSingleLogout?: boolean } = {},
  ) => {},
  entityRoleIdRef: { current: undefined },
  languageRef: { current: undefined },
  getTokenSilentlyRef: { current: undefined },
});

export const cache: InMemoryCache = new InMemoryCache({
  typePolicies: { GroupedBilling: { keyFields: ['id', 'billingCycleId'] } },
});

const OvApolloProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation(['shared']);
  const { showToast } = useGlobalToast();
  const [resetKey, setResetKey] = useState(0);
  const [settings, setSettings] = useState<AuthContextSettings>({});
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);

  const getTokenSilentlyRef = useRef<(() => Promise<string>) | undefined>();
  const isAppLogoutRef = useRef(false);
  const entityRoleIdRef = useRef<string>();
  const languageRef = useRef<LanguageCodes>();

  const appLogout = async ({
    reason,
    preventSingleLogout,
  }: {
    reason?: string;
    preventSingleLogout?: boolean;
  } = {}) => {
    if (!isEmbedded) {
      const redirectUrl = settings?.logoutUrl ?? settings?.auth0RedirectUri;
      const federated = settings?.enableSingleLogout && !preventSingleLogout;
      await auth0Logout({ redirectUrl, federated });
    }

    if (settings?.clientHostedLoginURL && settings?.useHostedLogin) {
      await clientHostedLogout(
        settings?.clientHostedLoginURL,
        settings?.logoutUrl ?? settings?.clientHostedLoginRedirectUri,
        reason,
      );
    }
    if (client) await client.clearStore();
    if (window?.localStorage) {
      window.localStorage.clear();
      if (reason && reason !== '') {
        window.localStorage.setItem('loggedOutMessage', reason);
      }
    }
    setResetKey((prevKey) => prevKey + 1); // Increment the key to force re-mount
  };

  const client = useMemo(
    () =>
      new ApolloClient({
        cache,
        link: from([
          onError(({ graphQLErrors, networkError, operation }) => {
            let preLogoutMessage = '';
            let userNeedsToBeLoggedOut = false;
            if (networkError) {
              if (
                'statusCode' in networkError &&
                (networkError as ServerError).statusCode === 401
              ) {
                showToast({
                  message: t('shared:validationError.sessionTimeout'),
                  severity: 'error',
                });
              } else {
                datadogLogs.logger.error(
                  `Network Error: cause: ${networkError.cause}, message: ${networkError.message}`,
                );
              }
            }
            if (graphQLErrors) {
              graphQLErrors.forEach(
                ({ message, locations, path, extensions }) => {
                  console.log(
                    `[GraphQL error]: Code: ${extensions?.code}, Message: ${message}, Operation: ${JSON.stringify(operation.query)}, Variables: ${JSON.stringify(
                      operation.variables,
                    )}, Location: ${JSON.stringify(locations)}, Path: ${path}`,
                  );
                },
              );

              if (
                !operation.variables.skipErrorHandler &&
                graphQLErrors.length > 0 &&
                (graphQLErrors[0].message || operation.variables.errorMessage)
              ) {
                let uniqueFields: any[] = [];
                const readableData: any =
                  graphQLErrors[0].extensions?.readableData;
                const userMessage = translateBackend(readableData?.userMessage);

                const validationErrorWithFields = `${t('shared:validationError.unique')} ${uniqueFields
                  .map((a) => a.path)
                  .join(', ')
                  .replace(/, ([^,]*)$/, ' or $1')}.`;

                if (readableData?.fields) {
                  uniqueFields = readableData.fields.filter(
                    (f: any) => Object.hasOwn(f, 'kind') && f.kind === 'unique',
                  );
                }

                if (
                  graphQLErrors[0].extensions?.code === 'VALIDATION' &&
                  readableData?.code === 11000
                ) {
                  showToast({
                    message:
                      userMessage ||
                      `${t('shared:validationError.unique')} ${Object.keys(readableData?.keyValue)[0]}.`,
                    severity: 'error',
                  });
                } else if (
                  graphQLErrors[0].extensions?.code === 'VALIDATION' &&
                  readableData?.fields &&
                  uniqueFields.length > 0
                ) {
                  showToast({
                    message: userMessage || validationErrorWithFields,
                    severity: 'error',
                  });
                } else if (
                  graphQLErrors[0].extensions?.code === 'TOKEN_REFRESH_REQUIRED'
                ) {
                  console.log(
                    'TOKEN_REFRESH_REQUIRED detected - processing logout',
                    {
                      errorCode: graphQLErrors[0].extensions?.code,
                      errorMessage: graphQLErrors[0].message,
                      userMessage,
                      operationName: operation.operationName,
                      timestamp: new Date().toISOString(),
                    },
                  );

                  userNeedsToBeLoggedOut = true;
                  preLogoutMessage =
                    userMessage ||
                    t('shared:validationError.tokenRefreshFailed');

                  showToast({
                    severity: 'error',
                    message: preLogoutMessage,
                  });
                } else if (graphQLErrors[0].extensions?.code === 11000) {
                  showToast({
                    message: t('shared:validationError.duplicateRecord'),
                    severity: 'error',
                  });
                } else if (
                  graphQLErrors[0].extensions?.code === 'NOT_ACCESSIBLE' &&
                  ((): boolean => {
                    const rd = graphQLErrors[0].extensions?.readableData as
                      | { errorCode?: string; message?: string }
                      | undefined;
                    return (
                      rd?.errorCode === 'ORG_USER_DOES_NOT_EXIST' ||
                      graphQLErrors[0].message ===
                        'OrganizationUser does not exist' ||
                      rd?.message === 'OrganizationUser does not exist'
                    );
                  })()
                ) {
                  showToast({
                    severity: 'error',
                    message: t(
                      'shared:backendIssuedError.organizationUserNotFound',
                    ),
                  });
                } else {
                  showToast({
                    message:
                      userMessage ||
                      (operation.variables.errorMessage ??
                        graphQLErrors[0].message),
                    severity: readableData?.userMessageSeverity ?? 'error',
                  });
                }
              }
            }

            if (userNeedsToBeLoggedOut === true) {
              console.warn('User needs to be logged out - scheduling logout', {
                preLogoutMessage,
                errorCode: graphQLErrors?.[0]?.extensions?.code,
                timestamp: new Date().toISOString(),
              });

              setTimeout(() => {
                appLogout();
              }, 5000); // <---- 5 seconds
              datadogLogs.logger.warn(
                'User session expired. Will log out in 10 seconds...',
                {
                  errorType: 'ApolloClientError',
                  step: 'PreLogout',
                  errorMessage: preLogoutMessage,
                  errorCode: graphQLErrors?.[0]?.extensions?.code,
                },
              );
            }
          }),
          setContext(async ({ operationName }, { headers }) => {
            let token: string | undefined;

            try {
              if (
                !(operationName && PUBLIC_ENDPOINTS.includes(operationName))
              ) {
                token = await getTokenSilentlyRef.current?.();
              }
            } catch (error) {
              datadogLogs.logger.error('Token Refresh Error', {
                error,
                operationName,
              });
              if (!isAppLogoutRef.current) {
                isAppLogoutRef.current = true;
                await appLogout();
              }

              throw error;
            }

            return {
              headers: {
                ...headers,
                ...(token && { authorization: `Bearer ${token}` }),
                ...(entityRoleIdRef.current && {
                  'x-role-id': entityRoleIdRef.current,
                }),
                ...(languageRef.current && {
                  'x-language': languageRef.current,
                }),
              },
            };
          }).concat(createHttpLink({ uri: CONFIG.app.graphqlClient })),
        ]),
      }),

    // TODO: To enhance handling for TOKEN_REFRESH_REQUIRED, remove the below 'disable line" and add appLogout
    // to the dependency array this will require a refactor of the appLogout function to be a hook
    // (e.g to wrap it in a useCallback)

    [showToast, t],
  );

  return (
    <AuthContext.Provider
      value={{
        isEmbedded,
        setIsEmbedded,
        permissions,
        setPermissions,
        settings,
        setSettings,
        appLogout,
        entityRoleIdRef,
        languageRef,
        getTokenSilentlyRef,
      }}
    >
      <ApolloProvider client={client}>
        <div key={resetKey}>{children}</div>
      </ApolloProvider>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);

export default OvApolloProvider;
